import React from "react";
import { Database, Globe2, Activity, ShieldCheck, FileCheck2, Layers } from "lucide-react";
import { PageHeader, CTABand } from "./Services";

const SECTIONS = [
  { icon: Layers, title: "Audience Segmentation", body: "We build audience segments using owned, licensed and partner data where rights and permissions exist. Segments are documented with their source, purpose and applicable permissions so we can plan, deliver and review activity without losing sight of provenance." },
  { icon: Globe2, title: "GEO Planning", body: "GEO planning aligns audience definition, channel mix and creative approach with market-specific rules. We work GEO by GEO across EU, UK, LATAM, APAC and emerging regions where the operator has a basis to market." },
  { icon: Activity, title: "Campaign Intelligence", body: "Performance signals — engagement, conversion, suppression and complaints — feed back into segment selection and channel weighting. Decisions are recorded, not improvised, so the next cycle starts from a documented baseline." },
  { icon: FileCheck2, title: "Data Validation and Enrichment", body: "Where appropriate, we validate and enrich data using lawful sources and contracted providers. Enrichment is scoped to documented use cases and supported by appropriate contractual terms." },
  { icon: ShieldCheck, title: "Marketing Permissions and Records", body: "We maintain records that link audiences to their sourcing, permissions and applicable opt-outs. Suppression instructions from operators and recipients are honoured and reapplied on cadence." },
  { icon: Database, title: "Responsible Data Usage", body: "We do not promote inflated audience numbers. Where we operate as a service provider, we act on documented client instructions. Where we own audiences, we maintain the rights to use them." },
];

export default function DataAudience() {
  React.useEffect(() => {
    document.title = "Data and Audience Strategy | PlayerReach";
  }, []);
  return (
    <div data-testid="data-audience-page">
      <PageHeader
        eyebrow="Data & Audience"
        title="Audience intelligence for acquisition teams."
        subtitle="PlayerReach works with first-party data, licensed data, partner data and customer data where appropriate rights and permissions exist. We focus on quality, legitimacy and responsible use — not unverifiable volume claims."
      />

      {/* Overview metric-style cards (no fake numbers) */}
      <section className="section-pad">
        <div className="container-pr">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { label: "Sourcing", value: "Owned · Licensed · Partner", caption: "Documented across the data lifecycle." },
              { label: "Markets", value: "EU · UK · LATAM · APAC", caption: "Activity planned GEO by GEO." },
              { label: "Channels", value: "Affiliate · Direct · Email · SMS", caption: "Mix planned around audience permission." },
            ].map((m) => (
              <div key={m.label} className="card-flat p-7">
                <p className="text-[10.5px] uppercase tracking-[0.2em] text-slate-500 font-mono-pr">{m.label}</p>
                <p className="font-display mt-2 text-2xl lg:text-3xl font-semibold text-slate-900">{m.value}</p>
                <p className="mt-2 text-sm text-slate-500">{m.caption}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-5">
            {SECTIONS.map((s, i) => (
              <div key={s.title} className="card-flat p-7 transition-transform hover:-translate-y-0.5" data-testid={`data-section-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-slate-50 border border-slate-200 text-[#0052FF]">
                    <s.icon className="h-5 w-5" />
                  </span>
                  <span className="text-xs font-mono-pr text-slate-400">0{i + 1}</span>
                </div>
                <h3 className="font-display mt-5 text-xl font-semibold text-slate-900">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance note */}
      <section className="pb-20 lg:pb-28">
        <div className="container-pr">
          <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 p-8 lg:p-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
              <div className="lg:col-span-2">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-md bg-white border border-slate-200 text-emerald-600">
                  <ShieldCheck className="h-6 w-6" />
                </span>
              </div>
              <div className="lg:col-span-10">
                <p className="overline">Compliance note</p>
                <p className="mt-2 text-slate-700 leading-relaxed text-base lg:text-lg">
                  Data activity must be supported by ownership, authority, lawful use rights, consent where required, contractual documentation and appropriate record keeping. PlayerReach assesses data sources and partner permissions before activation and on an ongoing basis.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
